import React, { useState, MouseEventHandler, useEffect } from 'react';
import styles from './Reservation.module.css';
import ReservationDetail from '../reservationDetail/ReservationDetail';
import ReservationInput from '../reservationInput/ReservationInput';
import navLeft from '../../assets/images/ArrowNavLeft.svg';
import { useTranslation } from 'react-i18next';
import { getBookingAdminToolConfig, getBookingReservationDetails } from '../../apiCall';
import { Helmet } from 'react-helmet';
import { trackPromise } from 'react-promise-tracker';
interface Props {
  deActiveSplashContent: (status: boolean) => void;
  splashContentBoxHandler: (status: boolean) => void;
}

const Reservation = ({ deActiveSplashContent, splashContentBoxHandler }: Props) => {
  const { t } = useTranslation();
  const [reservationElementsConfig, setReservationElementsConfig] = useState({
    showCustomerReservation: false,
    showReservationAssistance: false,
    showAgodaBrandLink: false,
    showPricelineBrandLink: false,
    showReservationAssistanceNumber: ''
  });
  const [showReservationDetail, setShowReservationDetail] = useState(false);
  const [reservationDetails, setReservationDetails] = useState(Object);
  const [showErrorText, setErrorText] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [messageHeader, setMessageHeader] = useState('');
  const [remainingAttempts, setRemainingAttempts] = useState('');
  const [showAccountLockout, setAccountLockout] = useState(false);

  const getPageTitleHandler = () => {
    if (messageHeader === 'sessionLogoutHeader') {
      return `${t('LoggedOutTitle')} | Booking.com`;
    } else if (messageHeader === 'tooManyAttemptsErrorHeader') {
      return `${t('AccessRestrictedTitle')} | Booking.com`;
    } else if (messageHeader === 'internalErrorHeader') {
      return `${t('ErrorTitle')} | Booking.com`;
    } else {
      return `Booking.com | ${t('OfficialSite')}`;
    }
  };

  useEffect(() => {
    const getAdminConfig = async () => {
      await trackPromise(
        getBookingAdminToolConfig()
          .then((res) => {
            if (res && res.settings.enableAdvanceFunctionallity) {
              setReservationElementsConfig({
                showCustomerReservation: res.settings.showCustLogin,
                showReservationAssistance: res.settings.showCustServiceTelephone,
                showAgodaBrandLink: res.settings.showAgodaBrandLink,
                showPricelineBrandLink: res.settings.showPricelineBrandLink,
                showReservationAssistanceNumber: res.settings.phNos[0]
              });
              splashContentBoxHandler(res.settings.enableAdvanceFunctionallity);
            }
          })
          .catch((error) => {
            error &&
              setReservationElementsConfig({
                showCustomerReservation: true,
                showReservationAssistance: false,
                showAgodaBrandLink: true,
                showPricelineBrandLink: true,
                showReservationAssistanceNumber: ''
              });
          })
      );
    };
    getAdminConfig();
  }, []);

  useEffect(() => {
    if (showAccountLockout) {
      setTimeout(function () {
        setAccountLockout(false);
        setReservationElementsConfig((prevState) => ({
          ...prevState,
          showCustomerReservation: true
        }));
      }, 300000);
    }
  }, [showAccountLockout]);

  const getBookingReservationDetailsHandler = async (reservationId: string, pinNumber: string) => {
    setErrorText(false);
    setRemainingAttempts('');
    await trackPromise(
      getBookingReservationDetails(reservationId, pinNumber)
        .then((response) => {
          if (response.responseCode === 200) {
            setReservationDetails(response.reservationViewDetails);
            setShowReservationDetail(true);
            setErrorText(false);
            deActiveSplashContent(true);
          } else if (response.responseCode === 429) {
            setErrorMessage('tooManyAttemptsErrorMessage');
            setMessageHeader('tooManyAttemptsErrorHeader');
            setAccountLockout(true);
            setReservationElementsConfig((prevState) => ({
              ...prevState,
              showCustomerReservation: false
            }));
          } else if (response.responseCode === 401 || response.responseCode === 404) {
            setErrorMessage('loginErrorMessage');
            setMessageHeader('loginErrorHeader');
            if (response.remainingAttempts) {
              setRemainingAttempts(response.remainingAttempts);
            }
            setErrorText(true);
          } else {
            setErrorMessage('internalErrorMessage');
            setMessageHeader('internalErrorHeader');
            setAccountLockout(true);
            setErrorText(true);
            setReservationElementsConfig((prevState) => ({
              ...prevState,
              showCustomerReservation: false
            }));
          }
        })
        .catch((error) => {
          error &&
            setReservationElementsConfig((prevState) => ({
              ...prevState
            }));
        })
    );
  };
  const handleReservationNavigation: MouseEventHandler<HTMLSpanElement> = (e) => {
    e.preventDefault();
    setShowReservationDetail(false);
    deActiveSplashContent(false);
    setErrorText(false);
  };

  const handleSessionTimeouts = (flag: boolean, msgHeader: string, errorMsg: string) => {
    setShowReservationDetail(flag);
    deActiveSplashContent(flag);
    setErrorText(!flag);
    setErrorMessage(errorMsg);
    setMessageHeader(msgHeader);
  };

  return (
    <>
      <Helmet>
        <title>{getPageTitleHandler()}</title>
      </Helmet>
      {showReservationDetail ? (
        <div className={styles.bookingReservationDetailContainer}>
          <img className={styles.navLeftArrow} src={navLeft} alt={'nav left arrow'} />

          <button
            className={styles.backReservationButton}
            data-testid="backReservationButtonTestId"
            onClick={handleReservationNavigation}>
            {t('reservation_back_button')}
          </button>
          <div className={styles.bookingServiceReservationDetailContainer}>
            <ReservationDetail
              reservationDetails={reservationDetails}
              handleSessionTimeouts={handleSessionTimeouts}
            />
          </div>
        </div>
      ) : (
        <div className={styles.reservationContainer} data-testid="reservationContainerDiv">
          <ReservationInput
            showErrorText={showErrorText}
            showAccountLockout={showAccountLockout}
            errorMessage={errorMessage}
            errorHeader={messageHeader}
            remainingAttempts={remainingAttempts}
            reservationElementsConfig={reservationElementsConfig}
            getBookingReservationDetailsHandler={getBookingReservationDetailsHandler}
          />
        </div>
      )}
    </>
  );
};

export default Reservation;
